import type { web_notifications } from "@dropbox/api-v2-client";
import { tagged } from "@mirage/service-logging";
import { IncompletePayloadException } from "@mirage/shared/errors/classes/notifications";
import type { BluenoteSurfaces } from "../service/api";

const logger = tagged("service-notifications");

// Copied from:desktop/dropbox/client/notifications/types.py and unlikely to change on the bluenote backend
// Unfortunately the server datatype is just number so this leaks down to the client
export enum NotificationReadStatus {
  UNREAD,
  READ,
  INVISIBLE,
}

// Copied from:desktop/dropbox/client/notifications/types.py and unlikely to change on the bluenote backend
export enum NotificationSeenState {
  UNSEEN = 1,
  SEEN = 2,
}

const BLUENOTE_TYPE_ID = 2200;

// Try to keep Bluenote specifics tightly scoped and private
type DashBluenotePayload = {
  title: string;
  message: string;
  isPersistent: boolean;
  actions: Actions;
  campaignId: number;
  targetObjectKey: string;
};

type Interaction = {
  openUrl?: {
    url: string;
  };
};

export type Actions = {
  primaryCta: Interaction;
  buttons: Interaction[];
};

export type DashNotification = web_notifications.Notification & {
  parsedPayload: DashBluenotePayload;
  timestampSeconds: number;
};

export function parseBluenotePayload(
  notification: web_notifications.Notification,
  surface: BluenoteSurfaces,
): DashNotification[] {
  if (notification.type_id !== BLUENOTE_TYPE_ID) {
    return [];
  }

  try {
    const payload = JSON.parse(notification.payload || "{}");
    const appSpecificPayload = payload[surface];
    if (!appSpecificPayload) {
      return [];
    }

    const parsedPayload = {
      title: appSpecificPayload.loud_params?.title ?? "",
      message: appSpecificPayload.loud_params?.message ?? "",
      isPersistent: appSpecificPayload.loud_params?.is_persistent ?? false,
      actions: appSpecificPayload.extra_payload?.actions ?? [],
      campaignId: appSpecificPayload.campaignId ?? 0,
      targetObjectKey: payload.target_object_key ?? "",
    };

    if (!parsedPayload.title || !parsedPayload.targetObjectKey) {
      throw new IncompletePayloadException(
        "Missing required params: loud title or target object key",
      );
    }

    // Rename this with the time unit in the name
    const timestampSeconds = notification.feed_time ?? 0;
    return [
      {
        ...notification,
        parsedPayload,
        timestampSeconds,
      },
    ];
  } catch (error) {
    if (error instanceof SyntaxError) {
      logger.error("Failed to parse bluenote payload (invalid json)", error);
      return [];
    }
    if (error instanceof IncompletePayloadException) {
      logger.warn("Invalid bluenote payload", { error: error.message });
      return [];
    }
    throw error;
  }
}
